import { GeneralHelper } from '@helpers';
import { Eye } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useDocuments } from '../DocumentsContext';

import styles from './DocumentFileLink.module.scss';

const VIEWABLE_EXTENSIONS = /\.(pdf|docx?)$/i;

export const isViewableDocument = (text: string): boolean => VIEWABLE_EXTENSIONS.test(text.trim());

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

export const DocumentFileLink = ({ filename }: { filename: string }) => {
  const documents = useDocuments();
  const [loading, setLoading] = useState(false);
  const trimmed = filename.trim();
  const url = documents.get(trimmed);

  const handleClick = useCallback(async () => {
    if (!url || loading) return;
    setLoading(true);
    try {
      const blob = await GeneralHelper.fetchFileBlob(url);
      // Re-wrap with correct MIME so the browser opens a viewer instead of downloading
      const ext = extensionOf(trimmed);
      const mime = MIME_BY_EXT[ext];
      const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
      const objectUrl = URL.createObjectURL(typed);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      GeneralHelper.downloadFile(url, trimmed);
    } finally {
      setLoading(false);
    }
  }, [url, trimmed, loading]);

  if (!url) return null;

  return (
    <button
      type="button"
      className={styles.link}
      onClick={handleClick}
      title={`Open ${trimmed}`}
      aria-label={`Open ${trimmed}`}
    >
      <Eye size={13} />
    </button>
  );
};
